import React, { useEffect, useState } from "react";
import { Grid, Box, Typography, Button, useTheme, Alert } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import HistoricalSignalsList from "./HistoricalSignalsList";
import PortfolioSummary from "./PortfolioSummary";
import MissingReturnsTickers from "../Popup/MissingReturnsTickers";

const HistoricalSignalsDisplay = ({
  strategies,
  strategyValObj,
  strategyVal,
  onDownload,
  hasData,
  historicalSignalSet,
  isFetchingHistoricalSignals,
}) => {
  const theme = useTheme();
  const [showMissingTickers, setShowMissingTickers] = useState(false);

  useEffect(() => {
    if (historicalSignalSet?.missingReturnsTickers?.length) {
      setShowMissingTickers(true);
    }
  }, [historicalSignalSet?.missingReturnsTickers]);

  const downloadCSV = () => {
    if (!historicalSignalSet?.excelReport || !strategyValObj) {
      toast.error("No Excel file available.");
      return;
    }

    try {
      // Remove data URL prefix if present
      const base64String = historicalSignalSet.excelReport.includes(",")
        ? historicalSignalSet.excelReport.split(",")[1]
        : historicalSignalSet.excelReport;

      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);

      // Force download instead of opening
      const link = document.createElement("a");
      link.href = url;
      const date = new Date();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const dateStr = `${month}-${day}-${year}`;
      let fileName = `historical_signals_${strategyVal}_${dateStr}.xlsx`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file.");
    }
  };

  // Don't show anything if no data
  if (!hasData && !isFetchingHistoricalSignals) {
    return null;
  }
  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>  
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "1.1rem",
              }}
            >
              Historical Signals
            </Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <button
                onClick={downloadCSV}
                disabled={!hasData || !historicalSignalSet?.excelReport}
                style={{
                  backgroundColor: !hasData || !historicalSignalSet?.excelReport ? "#cccccc" : "#2196f3",
                  color: !hasData || !historicalSignalSet?.excelReport ? "#666666" : "white",
                  fontWeight: 500,
                  textTransform: "none",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  cursor: !hasData || !historicalSignalSet?.excelReport ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontFamily: "inherit",
                  minHeight: "36px",
                  boxShadow: !hasData || !historicalSignalSet?.excelReport
                    ? "none"
                    : "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                  opacity: !hasData || !historicalSignalSet?.excelReport ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!hasData || !historicalSignalSet?.excelReport) return;
                  e.target.style.backgroundColor = "#1976d2";
                }}
                onMouseLeave={(e) => {
                  if (!hasData || !historicalSignalSet?.excelReport) return;
                  e.target.style.backgroundColor = "#2196f3";
                }}
              >
                <DownloadIcon style={{ fontSize: "20px" }} />
                Download CSV
              </button>
              <button
                onClick={onDownload}
                disabled={!hasData}
                style={{
                  backgroundColor: !hasData ? "#cccccc" : "#4caf50",
                  color: !hasData ? "#666666" : "white",
                  fontWeight: 500,
                  textTransform: "none",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  cursor: !hasData ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontFamily: "inherit",
                  minHeight: "36px",
                  boxShadow: !hasData
                    ? "none"
                    : "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                  opacity: !hasData ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!hasData) return;
                  e.target.style.backgroundColor = "#388e3c";
                }}
                onMouseLeave={(e) => {
                  if (!hasData) return;
                  e.target.style.backgroundColor = "#4caf50";
                }}
              >
                <DownloadIcon style={{ fontSize: "20px" }} />
                Download All
              </button>
            </Box>
          </Box>
          <HistoricalSignalsList
            strategies={strategies}
            strategyValObj={strategyValObj}
            strategyVal={strategyVal}
          />
        </Grid>
      </Grid>
      <MissingReturnsTickers
        open={showMissingTickers}
        onClose={() => setShowMissingTickers(false)}
        missingReturnsTickers={historicalSignalSet?.missingReturnsTickers}
      />
    </Box>
  );
};

export default HistoricalSignalsDisplay;
